import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { GripVertical, Plus, Trash2, RotateCcw, ChevronUp, ChevronDown } from "lucide-react";

const DEFAULTS = [
  "Project Created", "Requirement Gathering", "Requirement Approval", "Planning",
  "UI/UX Design", "Development", "Internal Testing", "Client Testing",
  "Client Feedback", "Changes/Revision", "Final Approval", "Deployment", "Project Completed",
];

export default function WorkflowEditor({ open, onOpenChange, project, onSaved }) {
  const [stages, setStages] = useState([]);
  const [currentStage, setCurrentStage] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    if (open) {
      setStages([...(project?.workflow_stages || DEFAULTS)]);
      setCurrentStage(project?.current_stage || "");
    }
  }, [open, project]);

  const move = (from, to) => {
    if (to < 0 || to >= stages.length) return;
    const next = [...stages];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setStages(next);
  };

  const rename = (i, val) => {
    const next = [...stages];
    // If the current_stage is the one being renamed, follow the rename
    if (currentStage === next[i]) setCurrentStage(val);
    next[i] = val;
    setStages(next);
  };

  const remove = (i) => {
    if (stages.length <= 2) { toast.error("Need at least 2 stages"); return; }
    const removed = stages[i];
    const next = stages.filter((_, idx) => idx !== i);
    setStages(next);
    if (currentStage === removed) setCurrentStage(next[Math.min(i, next.length - 1)]);
  };

  const add = () => setStages([...stages, `Stage ${stages.length + 1}`]);
  const reset = () => { setStages([...DEFAULTS]); setCurrentStage(DEFAULTS[0]); };

  const save = async () => {
    const clean = stages.map(s => s.trim()).filter(Boolean);
    if (clean.length < 2) { toast.error("Need at least 2 non-blank stages"); return; }
    if (new Set(clean).size !== clean.length) { toast.error("Stage names must be unique"); return; }
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/workflow`, {
        stages: clean,
        current_stage: clean.includes(currentStage) ? currentStage : clean[0],
      });
      toast.success("Pipeline saved");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e, i) => { e.preventDefault(); if (dragIdx === null || dragIdx === i) return; move(dragIdx, i); setDragIdx(i); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-neutral-200 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="workflow-editor">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Customise Pipeline</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">
            Rename, reorder, add or remove workflow stages. Drag rows to reorder or use the arrow buttons.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {stages.map((s, i) => (
            <div
              key={i}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={(e) => onDragOver(e, i)}
              onDragEnd={() => setDragIdx(null)}
              className={`flex items-center gap-2 rounded-lg border p-2 ${currentStage === s ? "border-orange-300 bg-orange-50" : "border-neutral-200 bg-white"}`}
              data-testid={`stage-row-${i}`}
            >
              <GripVertical className="w-4 h-4 text-neutral-400 cursor-grab" />
              <span className="text-[10px] font-mono text-neutral-500 w-6 text-center">{i + 1}</span>
              <Input
                value={s}
                onChange={(e) => rename(i, e.target.value)}
                className="flex-1 h-8 text-sm bg-white border-neutral-200"
                data-testid={`stage-input-${i}`}
              />
              <div className="flex items-center">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(i, i - 1)} disabled={i === 0} data-testid={`stage-up-${i}`}><ChevronUp className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(i, i + 1)} disabled={i === stages.length - 1} data-testid={`stage-down-${i}`}><ChevronDown className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-700" onClick={() => remove(i)} data-testid={`stage-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={add} className="border-neutral-200" data-testid="stage-add"><Plus className="w-4 h-4 mr-1" /> Add Stage</Button>
          <Button variant="ghost" onClick={reset} data-testid="stage-reset" className="text-neutral-600"><RotateCcw className="w-4 h-4 mr-1" /> Reset to Default</Button>
        </div>

        <div className="space-y-1.5 pt-2 border-t border-neutral-200 mt-2">
          <div className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Current Stage</div>
          <Select value={currentStage} onValueChange={setCurrentStage}>
            <SelectTrigger className="bg-white border-neutral-200" data-testid="stage-current"><SelectValue /></SelectTrigger>
            <SelectContent>
              {stages.filter(Boolean).map((s, i) => <SelectItem key={i} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="stage-save">
            {busy ? "Saving..." : "Save Pipeline"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
